import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AdminPage, { AdminCard, SectionHeader } from './components/admin/AdminPage';
import ListActions from './components/admin/ListActions';
import { colour, pillSx, primaryButtonSx } from './components/admin/tokens';

import { fetchStaffAccess, saveStaffAccess, revokeStaffAccess } from './api/staffAccess';
import { canManageAccess } from './utils/adminSession';

/**
 * Who may use the staff screens, and for which campuses.
 *
 * Reachable only by someone named in ADMIN_SUPER_USERS — the server refuses everybody else, and
 * this screen says so plainly rather than showing an empty table. Nothing here can make another
 * access manager: that stays a deliberate change to the environment.
 */

const BLANK = {
    staffMemberId: null,
    fullName: '',
    email: '',
    employeeId: '',
    accessLevel: 'view',
    team: 'enrolment',
    allCampuses: false,
    campuses: [],
    // Granting access to others. Off by default: it is the one setting that lets somebody widen
    // everybody's reach, so it should never be the thing you forget to untick.
    canManageAccess: false,
    notes: '',
};

/**
 * What the level means, which is not the same question for the two teams.
 *
 * It was written for the enrolment team alone, and the control was hidden from accounts entirely
 * because "approving is the enrolment team's, so for accounts the level has nothing to act on".
 * That was true when it was written and is not true now: the accounts team gained two actions that
 * the level gates, and hiding the control left AR unable to be granted either of them.
 *
 *   recording a payment they have verified — POST /api/admin/payments/record refuses view-only, and
 *   it is the action that puts a receipt on a family's row and completes their re-enrolment;
 *
 *   approving a signed manual entry — mayApproveSession waives the TEAM check for those, because
 *   accounts entered the form and uploaded it themselves, so either team may approve one as long
 *   as they are not view-only.
 *
 * What the level does NOT give the accounts team is approving an online family's changes. That is
 * refused by team and not by level — mayApproveForCampus needs seesFamilyDetail, which is
 * `team !== 'accounts'` and so false for them whatever their level. Worth saying in the modal,
 * because somebody granting this needs to know its edges.
 */
const LEVEL_TEXT = {
    enrolment: {
        view: 'Can read what families changed, but cannot approve it.',
        approve: 'Can read the changes and approve them for TASS.',
    },
    accounts: {
        view: 'Can read the money and the payments, but cannot record or complete anything.',
        approve: 'Can also record a payment they have verified, which completes that family, and '
            + 'approve a signed manual entry. Still cannot approve an online family\'s changes — '
            + 'that is the enrolment team\'s, whatever the level.',
    },
};

/** Both teams for somebody marked 'both', who does each job. */
LEVEL_TEXT.both = LEVEL_TEXT.accounts;

const levelText = (team, level) => (LEVEL_TEXT[team] || LEVEL_TEXT.enrolment)[level];

/**
 * The two jobs these screens serve, and what each one is shown.
 *
 * This is not a second permission on top of the level — it is which half of a re-enrolment the
 * person works in. Somebody on the accounts team never sees the approval queue, so the level
 * above only means anything for the enrolment team.
 */
const TEAM_TEXT = {
    enrolment: {
        label: 'Enrolment team',
        detail: 'Parent and student details, Medicare cards, what changed, and approving it.',
    },
    accounts: {
        label: 'Accounts team',
        detail: 'Health care cards, what each family was charged, paid and used in credit.',
    },
    both: {
        label: 'Both',
        detail: 'Everything on both screens. For someone who genuinely does both jobs.',
    },
};

/**
 * Who has access, for paper and for a spreadsheet.
 *
 * Asked for because this is the answer to "who can see our families' details", and that question
 * comes from outside the AR team — from an audit, or from somebody deciding whose access to
 * withdraw after a resignation. Reading it off the screen and retyping it is how a name gets
 * missed.
 *
 * Three things the screen says in a place a column cannot: managing access is a chip inside the
 * Team cell, "Not applicable" stands in for the level on the accounts team because approving is
 * not their job, and a person whose access was withdrawn is on the table at half opacity. Each
 * one gets a column of its own here, because opacity does not survive a CSV.
 */
export const ACCESS_COLUMNS = [
    { header: 'Name', value: (row) => row.fullName || '' },
    {
        header: 'Signs in as',
        value: (row) => [row.email, row.employeeId].filter(Boolean).join(' · '),
    },
    {
        header: 'Campuses',
        value: (row) => (row.allCampuses ? 'All campuses' : (row.campuses || []).join(', ')),
    },
    { header: 'Team', value: (row) => TEAM_TEXT[row.team]?.label || 'Enrolment team' },
    { header: 'Manages access', value: (row) => (row.canManageAccess ? 'Yes' : 'No') },
    { header: 'Given by', value: (row) => row.accessGrantedBy || '' },
    // Shown for every team. It said "Not applicable" for accounts, on the grounds that approving
    // was not theirs — but the level now decides whether they can record a payment they have
    // verified, so for them it is the difference between reading the reconciliation and acting on it.
    { header: 'Can', value: (row) => (row.accessLevel === 'approve' ? 'Approve' : 'View only') },
    { header: 'Access', value: (row) => (row.isActive ? 'Current' : 'Withdrawn') },
];

const StaffAccess = () => {
    const [staff, setStaff] = useState([]);
    const [campusOptions, setCampusOptions] = useState([]);
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [editing, setEditing] = useState(null);
    const [removing, setRemoving] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            const data = await fetchStaffAccess();
            setStaff(data.staff || []);
            setCampusOptions(data.campuses || []);
            setDomains(data.domains || []);
        } catch (error) {
            setLoadError(
                error?.response?.status === 403
                    ? 'Your sign-in does not include handing out access. Ask whoever set up this environment.'
                    : error?.response?.data?.error || 'Could not load who has access.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const domainHint = useMemo(
        () => (domains.length ? `Must be ${domains.join(' or ')}` : ''),
        [domains]
    );

    const save = async () => {
        setSaving(true);
        setSaveError('');

        try {
            const data = await saveStaffAccess({
                ...editing,
                // All campuses and a list of them are the same decision asked two ways; the
                // server keeps whichever the checkbox says.
                campuses: editing.allCampuses ? [] : editing.campuses,
            });
            setStaff(data.staff || []);
            setEditing(null);
        } catch (error) {
            setSaveError(error?.response?.data?.error || 'Could not save that access.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const person = removing;
        setRemoving(null);

        try {
            const data = await revokeStaffAccess(person.staffMemberId);
            setStaff(data.staff || []);
        } catch (error) {
            setLoadError(error?.response?.data?.error || 'Could not remove that access.');
        }
    };

    const toggleCampus = (campus) => {
        setEditing((current) => ({
            ...current,
            campuses: current.campuses.includes(campus)
                ? current.campuses.filter((name) => name !== campus)
                : [...current.campuses, campus],
        }));
    };

    // An address or an employee number: one of them has to identify the person.
    const identified = Boolean(editing?.email?.trim() || editing?.employeeId?.trim());
    const campusChosen = Boolean(editing?.allCampuses || editing?.campuses?.length);

    return (
        <AdminPage
            title="Who can see and approve re-enrolments"
            lede="Each person is given the campuses they look after, the team they are on — which decides what their screen shows — and whether they can approve changes. Access takes effect at their next sign-in."
            actions={!loading && (
                <Button
                    variant="contained"
                    onClick={() => { setSaveError(''); setEditing({ ...BLANK }); }}
                    sx={primaryButtonSx}
                >
                    Give someone access
                </Button>
            )}
        >

            {loadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{loadError}</Alert>}

            {loading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
            ) : (
                <>
                    <AdminCard>
                    {/* This card had no header of its own. One is added here so the print and
                        export have the place every other staff screen keeps them — there is no
                        DataGrid on this screen to carry a toolbar. */}
                    <SectionHeader title="Who has access">
                        <ListActions
                            title="Who can see and approve re-enrolments"
                            columns={ACCESS_COLUMNS}
                            rows={staff}
                            fileName="staff-access"
                        />
                    </SectionHeader>
                    <Table size="small" sx={{ '& td, & th': { fontSize: '13px' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Signs in as</TableCell>
                                <TableCell>Campuses</TableCell>
                                <TableCell>Team</TableCell>
                                <TableCell>Can</TableCell>
                                <TableCell align="right">&nbsp;</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {staff.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ color: colour.muted }}>
                                        Nobody has been given access yet. Until then, only the
                                        employee numbers named in this environment can sign in.
                                    </TableCell>
                                </TableRow>
                            )}

                            {staff.map((person) => (
                                <TableRow key={person.staffMemberId} sx={{ opacity: person.isActive ? 1 : 0.5 }}>
                                    <TableCell>{person.fullName || '—'}</TableCell>
                                    <TableCell>
                                        {person.email || ''}
                                        {person.email && person.employeeId ? ' · ' : ''}
                                        {person.employeeId || ''}
                                    </TableCell>
                                    <TableCell>
                                        {person.allCampuses
                                            ? <Chip size="small" label="All campuses" sx={pillSx('info')} />
                                            : (person.campuses.join(', ') || '—')}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={TEAM_TEXT[person.team]?.label || 'Enrolment team'}
                                                sx={pillSx(person.team === 'accounts' ? 'info' : 'neutral')}
                                            />
                                            {/* Who can hand out access and reach Settings. Shown to
                                                everybody who can see this screen, not only to other
                                                managers: "who can do this" should be answerable by
                                                looking, which is the thing the environment list could
                                                never be asked. */}
                                            {person.canManageAccess && (
                                                <Chip
                                                    size="small"
                                                    label="Manages access"
                                                    sx={pillSx('warn')}
                                                    title={person.accessGrantedBy
                                                        ? `Given by ${person.accessGrantedBy}`
                                                        : undefined}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {/* Shown for every team. It read "Not applicable" for
                                            accounts, on the grounds that approving was not theirs
                                            — but the level now decides whether they can record a
                                            payment they have verified, so for them it is the
                                            difference between reading the reconciliation and
                                            being able to act on it. */}
                                        <Chip
                                            size="small"
                                            label={person.accessLevel === 'approve' ? 'Approve' : 'View only'}
                                            sx={pillSx(person.accessLevel === 'approve' ? 'good' : 'neutral')}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {person.isActive ? (
                                            <>
                                                <Button
                                                    size="small"
                                                    sx={{ textTransform: 'none' }}
                                                    onClick={() => { setSaveError(''); setEditing({ ...BLANK, ...person }); }}
                                                >
                                                    Change
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    sx={{ textTransform: 'none' }}
                                                    onClick={() => setRemoving(person)}
                                                >
                                                    Remove
                                                </Button>
                                            </>
                                        ) : (
                                            <Typography sx={{ fontSize: '12px', color: colour.muted }}>
                                                Access withdrawn
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </AdminCard>
                </>
            )}

            <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editing?.staffMemberId ? 'Change access' : 'Give someone access'}
                </DialogTitle>
                <DialogContent>
                    {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

                    <TextField
                        fullWidth
                        label="Name"
                        value={editing?.fullName || ''}
                        onChange={(event) => setEditing({ ...editing, fullName: event.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        fullWidth
                        label="AIC email address"
                        placeholder="name@aic.wa.edu.au"
                        helperText={domainHint}
                        value={editing?.email || ''}
                        onChange={(event) => setEditing({ ...editing, email: event.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Employee number"
                        helperText="Only needed while the temporary password sign-in is in use."
                        value={editing?.employeeId || ''}
                        onChange={(event) => setEditing({ ...editing, employeeId: event.target.value })}
                        sx={{ mb: 3 }}
                    />

                    <FormLabel sx={{ fontSize: '13px', fontWeight: 600 }}>
                        Which team are they on
                    </FormLabel>
                    <Typography sx={{ fontSize: '12px', color: colour.muted, mb: 0.5 }}>
                        This decides what their screens show.
                    </Typography>
                    <RadioGroup
                        value={editing?.team || 'enrolment'}
                        onChange={(event) => setEditing({ ...editing, team: event.target.value })}
                        sx={{ mb: 2 }}
                    >
                        {['enrolment', 'accounts', 'both'].map((team) => (
                            <FormControlLabel
                                key={team}
                                value={team}
                                control={<Radio size="small" />}
                                label={
                                    <Box>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {TEAM_TEXT[team].label}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: colour.muted }}>
                                            {TEAM_TEXT[team].detail}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>

                    {/* Asked of every team now. It was hidden from accounts, which left AR unable
                        to be granted the two things the level gates for them — recording a payment
                        they have verified, and approving a signed manual entry. See LEVEL_TEXT. */}
                    <FormLabel sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {editing?.team === 'enrolment' ? 'Can they approve changes' : 'What can they act on'}
                    </FormLabel>
                    <RadioGroup
                        value={editing?.accessLevel || 'view'}
                        onChange={(event) => setEditing({ ...editing, accessLevel: event.target.value })}
                        sx={{ mb: 2 }}
                    >
                        {['view', 'approve'].map((level) => (
                            <FormControlLabel
                                key={level}
                                value={level}
                                control={<Radio size="small" />}
                                label={
                                    <Box>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {level === 'view' ? 'View only' : 'View and approve'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: colour.muted }}>
                                            {levelText(editing?.team || 'enrolment', level)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>

                    <FormLabel sx={{ fontSize: '13px', fontWeight: 600, display: 'block', mt: 1 }}>
                        Which campuses
                    </FormLabel>
                    <FormControlLabel
                        // Not display:block — that overrides the inline-flex row and drops the
                        // label under the checkbox. Flex keeps them side by side on their own line.
                        sx={{ display: 'flex', alignItems: 'center', ml: 0, mt: 0.5 }}
                        control={
                            <Checkbox
                                size="small"
                                checked={Boolean(editing?.allCampuses)}
                                onChange={(event) => setEditing({ ...editing, allCampuses: event.target.checked })}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '14px' }}>
                                Every campus, including any opened later
                            </Typography>
                        }
                    />

                    {/*
                      * The right to hand out access, and to reach Settings.
                      *
                      * Until now this lived only in ADMIN_SUPER_USERS, so delegating it meant editing
                      * an environment file and redeploying. The environment list still works and is
                      * still checked first, as the way back in if every grant here were revoked.
                      *
                      * Shown only to somebody who already holds it. The server refuses anybody else
                      * (requireAccessManager on the route, and the service checks again), so this is
                      * the convenience and not the boundary.
                      */}
                    {canManageAccess() && (
                        <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: colour.warnTint, border: `1px solid ${colour.warnBorder}` }}>
                            <FormControlLabel
                                sx={{ display: 'flex', alignItems: 'flex-start', ml: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(editing?.canManageAccess)}
                                        onChange={(event) => setEditing({
                                            ...editing, canManageAccess: event.target.checked,
                                        })}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: colour.warnInk }}>
                                            Can give access to other people, and change Settings
                                        </Typography>
                                        <Typography sx={{ fontSize: '12.5px', color: colour.warnInk, mt: 0.25 }}>
                                            They will be able to grant this to others too. Every grant records who
                                            made it and when.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    )}

                    {!editing?.allCampuses && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {campusOptions.map((campus) => (
                                <FormControlLabel
                                    key={campus}
                                    sx={{ minWidth: '45%' }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(editing?.campuses?.includes(campus))}
                                            onChange={() => toggleCampus(campus)}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '14px' }}>{campus}</Typography>}
                                />
                            ))}
                        </Box>
                    )}

                    {/* This used to be a dropdown whose choices were "Enrolment team" and
                        "Accounts team" — the same question the team setting above now asks, and
                        answering both was either duplication or a contradiction. What is left is
                        the part it could say and the team cannot. */}
                    <TextField
                        fullWidth
                        label="Note (optional)"
                        placeholder="Covering admissions until February"
                        value={editing?.notes || ''}
                        onChange={(event) => setEditing({ ...editing, notes: event.target.value })}
                        sx={{ mt: 3 }}
                        helperText="Anything the next person reading this table should know."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={saving || !identified || !campusChosen}
                        onClick={save}
                        sx={primaryButtonSx}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save access'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)}
                PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Remove access?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '14px' }}>
                        {removing?.fullName || removing?.email || removing?.employeeId} will not be
                        able to sign in. Anything they have already approved keeps their name on it.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoving(null)} sx={{ textTransform: 'none' }}>Keep access</Button>
                    <Button color="error" variant="contained" onClick={remove} sx={{ textTransform: 'none' }}>
                        Remove access
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPage>
    );
};

export default StaffAccess;
